/**
 * Customer hooks using TanStack Query
 */

import { useQuery } from '@tanstack/react-query'
import { customerService } from '@/api'
import type {
  CustomerFilterParams,
  CustomerResponse,
  CustomerListResponse,
  CustomerReferralResponse,
} from '@/types/api'

/**
 * Query keys for customers
 */
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (filters: CustomerFilterParams) => [...customerKeys.lists(), filters] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  referrals: (id: string) => [...customerKeys.detail(id), 'referrals'] as const,
}

/**
 * Hook to get paginated customers
 */
export function useCustomers(params?: CustomerFilterParams) {
  return useQuery<CustomerListResponse>({
    queryKey: customerKeys.list(params || {}),
    queryFn: () => customerService.getCustomers(params),
  })
}

/**
 * Hook to get a single customer
 */
export function useCustomer(id: string) {
  return useQuery<CustomerResponse>({
    queryKey: customerKeys.detail(id),
    queryFn: () => customerService.getCustomer(id),
    enabled: !!id,
  })
}

/**
 * Hook to get customer referrals
 */
export function useCustomerReferrals(id: string) {
  return useQuery<CustomerReferralResponse>({
    queryKey: customerKeys.referrals(id),
    queryFn: () => customerService.getCustomerReferrals(id),
    enabled: !!id,
  })
}
